import { LucideIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { ExecutiveInsightItem, InsightNavTarget } from '../../services/dashboard'

type InsightInput = string | ExecutiveInsightItem

interface AIInsightsCardProps {
  title: string
  icon: LucideIcon
  iconBgColor: string
  iconColor: string
  items: InsightInput[]
  itemColor?: string
}

const URGENCY_STYLES: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-gray-100 text-gray-600',
}

function normalize(item: InsightInput): ExecutiveInsightItem {
  if (typeof item === 'string') return { summary: item }
  return item
}

// Map an insight's nav target to a frontend route. Returns null when the
// target is missing required ids or uses an unknown kind, so the UI can
// fall back to a non-clickable item rather than navigating somewhere
// confusing.
function navToRoute(nav: InsightNavTarget | null | undefined): string | null {
  if (!nav || !nav.kind) return null
  switch (nav.kind) {
    case 'project':
      if (!nav.id) return null
      return nav.tab ? `/project/${nav.id}/${nav.tab}` : `/project/${nav.id}`
    case 'projects':
      return '/project'
    case 'task':
      if (!nav.id) return null
      return `/task/${nav.id}`
    case 'tasks':
      return '/task'
    case 'portfolio':
      if (!nav.id) return null
      return `/portfolio/${nav.id}`
    case 'program':
      if (!nav.id) return null
      return `/program/${nav.id}`
    case 'risks':
      return '/risks'
    default:
      return null
  }
}

export function AIInsightsCard({
  title,
  icon: Icon,
  iconBgColor,
  iconColor,
  items,
  itemColor = '#486581',
}: AIInsightsCardProps) {
  const navigate = useNavigate()
  return (
    <div className="bg-white p-4 rounded-lg border-2 border-[#bcccdc] shadow-md hover:shadow-lg transition-shadow h-fit">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[#f0f4f8]">
        <div className={`p-1.5 ${iconBgColor} rounded`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
        </div>
        <h2 className="text-[#102a43]">{title}</h2>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-6 text-[#486581]">
          <p className="text-xs">No items at this time.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((raw, index) => {
            const item = normalize(raw)
            const urgencyClass = item.urgency ? URGENCY_STYLES[item.urgency] : null
            const route = navToRoute(item.nav)
            const isClickable = route !== null

            const body = (
              <>
                <div className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: itemColor }}></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <p className="text-sm leading-tight flex-1 text-left" style={{ color: itemColor }}>{item.summary}</p>
                    {urgencyClass && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${urgencyClass}`}>
                        {item.urgency}
                      </span>
                    )}
                  </div>
                  {item.reasoning && (
                    <p className="text-xs text-[#486581] mt-1 leading-snug text-left">{item.reasoning}</p>
                  )}
                </div>
              </>
            )

            return (
              <li key={index}>
                {isClickable ? (
                  <button
                    type="button"
                    onClick={() => navigate(route!)}
                    className="w-full flex gap-2 p-2 bg-[#f0f4f8] rounded hover:bg-[#d9e2ec] cursor-pointer transition-colors text-left focus:outline-none focus:ring-2 focus:ring-[#486581]"
                    aria-label={`View details for ${item.summary}`}
                  >
                    {body}
                  </button>
                ) : (
                  <div className="flex gap-2 p-2 bg-[#f0f4f8] rounded">{body}</div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

